import React, { useMemo, useState, useId, useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { authFetch, API_BASE } from "../auth";
import "./PriceBasedRunningMan.css";
import TickerAutocomplete from "../components/TickerAutocomplete";
import TickerIcon from "../components/TickerIcon";
import TickerLabel from "../components/TickerLabel";
import PropTypes from "prop-types";

const LEGS = ["core", "lotto", "hedge"];
const POLL_MS = 5000;

function parseTickers(raw) {
  return raw
    .split(",")
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
}

function createEmptyTicker() {
  const base = {
    order_start_time: "",
    order_expiry_time: "",
    buy_condition: "",
    buy_price: "",
    buy_price_upper: "",
    threshold_percent: "",
    expiry_date: "",
    per_leg_expiry: false,
  };

  LEGS.forEach((leg) => {
    base[`${leg}_expiry_date`] = "";
    base[`${leg}_strike_mode`] = "";
    base[`${leg}_exact_strike`] = "";
    base[`${leg}_strike_pct`] = "";
    base[`${leg}_dollar_dev`] = "";
    base[`${leg}_premium_mode`] = "pct";
    base[`${leg}_premium_pct`] = "";
    base[`${leg}_direction`] = "";
    base[`amount_to_invest_${leg}`] = "";
  });

  return base;
}

// Enabling per-leg expiry pre-fills blank leg dates from the ticker-wide date.
function setPerLegExpiry(data, enabled) {
  const next = { ...data, per_leg_expiry: enabled };
  if (enabled) {
    LEGS.forEach((leg) => {
      if (!next[`${leg}_expiry_date`]) next[`${leg}_expiry_date`] = data.expiry_date || "";
    });
  }
  return next;
}

function safeId(str) {
  return String(str).replace(/[^a-z0-9_-]/gi, "_");
}

/* ---------- validation ---------- */

function validateForm(formData) {
  const errs = new Set();

  Object.entries(formData).forEach(([ticker, d]) => {
    const key = (field) => `${ticker}.${field}`;

    if (!d.buy_condition) errs.add(key("buy_condition"));
    if (!d.per_leg_expiry && !d.expiry_date) errs.add(key("expiry_date"));

    const cond = d.buy_condition;
    if (cond === "between") {
      if (!d.buy_price) errs.add(key("buy_price"));
      if (!d.buy_price_upper) errs.add(key("buy_price_upper"));
    } else if (cond === ">" || cond === "<" || cond === "=") {
      if (!d.buy_price) errs.add(key("buy_price"));
    }
    if (cond === "=" && !d.threshold_percent) {
      errs.add(key("threshold_percent"));
    }

    LEGS.forEach((leg) => {
      if (d.per_leg_expiry && !d[`${leg}_expiry_date`]) {
        errs.add(key(`${leg}_expiry_date`));
      }
      if (!d[`${leg}_strike_mode`]) errs.add(key(`${leg}_strike_mode`));
      if (!d[`${leg}_premium_pct`]) errs.add(key(`${leg}_premium_pct`));
      if (!d[`${leg}_direction`]) errs.add(key(`${leg}_direction`));
      if (!d[`amount_to_invest_${leg}`]) errs.add(key(`amount_to_invest_${leg}`));

      const mode = d[`${leg}_strike_mode`];
      if (mode === "exact" && !d[`${leg}_exact_strike`]) {
        errs.add(key(`${leg}_exact_strike`));
      }
      if (mode === "pct" && !d[`${leg}_strike_pct`]) {
        errs.add(key(`${leg}_strike_pct`));
      }
      if (mode === "dollar" && !d[`${leg}_dollar_dev`]) {
        errs.add(key(`${leg}_dollar_dev`));
      }
    });
  });

  return errs;
}

/* ---------- status helpers ---------- */

const STATUS_LABELS = {
  active: "Active",
  filled: "Filled",
  expired: "Expired",
  cancelled: "Cancelled",
  error: "Error",
};

/* ---------- Field component ---------- */

function Field({ label, hint, children, id, controlClassName, error }) {
  const hintId = hint ? `${id}-hint` : undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        id,
        "aria-describedby": hintId,
        className: ["pbControl", controlClassName].filter(Boolean).join(" "),
      })
    : children;

  return (
    <div className={`pbField ${error ? "pbField--error" : ""}`}>
      <label className="pbField__label" htmlFor={id}>
        {label}
      </label>

      {hint ? (
        <div className="pbField__hint" id={hintId}>
          {hint}
        </div>
      ) : null}

      {control}
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.node.isRequired,
  hint: PropTypes.node,
  id: PropTypes.string.isRequired,
  children: PropTypes.element.isRequired,
  controlClassName: PropTypes.string,
  error: PropTypes.bool,
};

Field.defaultProps = {
  hint: null,
  controlClassName: "",
  error: false,
};

/* ---------- EditOrderModal ---------- */

function EditOrderModal({ orderId, ticker, initialData, onSave, onClose }) {
  const uid = useId();
  const [data, setData] = useState(() => ({ ...createEmptyTicker(), ...initialData }));
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    if (field === "per_leg_expiry") {
      setData((prev) => setPerLegExpiry(prev, value));
      return;
    }
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(orderId, data);
    setSaving(false);
  };

  const tSafe = safeId(ticker);

  return (
    <div
      className="pbModal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pb-edit-title"
    >
      <div className="pbModal pbModal--edit">
        <div className="pbModal__header">
          <div>
            <h2 className="pbModal__title" id="pb-edit-title">
              Edit Order &mdash; {ticker}
            </h2>
            <p className="pbModal__subtitle">
              Modify parameters for this active order. Changes take effect on the next monitoring cycle.
            </p>
          </div>
        </div>

        <div className="pbModal__body">
          {/* Entry trigger section */}
          <div className="pbSection">
            <div className="pbSection__title">Entry trigger</div>
            <div className="pbGrid pbGrid--3">
              <Field
                label="Order start time"
                hint="Leave blank to start immediately."
                id={`pb-edit-${uid}-${tSafe}-start`}
              >
                <input
                  type="time"
                  step="1"
                  value={data.order_start_time || ""}
                  onChange={(e) => handleChange("order_start_time", e.target.value)}
                />
              </Field>

              <Field
                label="Order expiry time"
                hint="Leave blank to keep checking."
                id={`pb-edit-${uid}-${tSafe}-expiry`}
              >
                <input
                  type="time"
                  step="1"
                  value={data.order_expiry_time || ""}
                  onChange={(e) => handleChange("order_expiry_time", e.target.value)}
                />
              </Field>

              <Field
                label="Expiry date"
                hint={data.per_leg_expiry ? "Set per leg below." : "Option expiry date."}
                id={`pb-edit-${uid}-${tSafe}-date`}
              >
                <input
                  type="date"
                  value={data.expiry_date || ""}
                  disabled={!!data.per_leg_expiry}
                  onChange={(e) => handleChange("expiry_date", e.target.value)}
                />
              </Field>

              <Field
                label="Buy condition"
                hint="How price should compare."
                id={`pb-edit-${uid}-${tSafe}-cond`}
              >
                <select
                  value={data.buy_condition || ""}
                  onChange={(e) => handleChange("buy_condition", e.target.value)}
                >
                  <option value="">Select&hellip;</option>
                  <option value=">">&gt; Above</option>
                  <option value="<">&lt; Below</option>
                  <option value="=">= At</option>
                  <option value="between">Between</option>
                </select>
              </Field>

              {data.buy_condition === "between" ? (
                <>
                  <Field
                    label="Lower price"
                    hint="Buy when price is at or above this."
                    id={`pb-edit-${uid}-${tSafe}-price`}
                  >
                    <input
                      type="number"
                      step="0.01"
                      value={data.buy_price || ""}
                      onChange={(e) => handleChange("buy_price", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Upper price"
                    hint="Buy when price is at or below this."
                    id={`pb-edit-${uid}-${tSafe}-price-upper`}
                  >
                    <input
                      type="number"
                      step="0.01"
                      value={data.buy_price_upper || ""}
                      onChange={(e) => handleChange("buy_price_upper", e.target.value)}
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field
                    label="Buy price"
                    hint="Target price for the condition."
                    id={`pb-edit-${uid}-${tSafe}-price`}
                  >
                    <input
                      type="number"
                      step="0.01"
                      value={data.buy_price || ""}
                      onChange={(e) => handleChange("buy_price", e.target.value)}
                    />
                  </Field>
                  {data.buy_condition === "=" && (
                    <Field
                      label="Threshold %"
                      hint="Allowed % deviation."
                      id={`pb-edit-${uid}-${tSafe}-threshold`}
                    >
                      <input
                        type="number"
                        step="0.01"
                        value={data.threshold_percent || ""}
                        onChange={(e) => handleChange("threshold_percent", e.target.value)}
                      />
                    </Field>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Legs section */}
          <div className="pbSection">
            <div className="pbSection__head">
              <div className="pbSection__title">Legs</div>
              <label className="pbCheck">
                <input
                  type="checkbox"
                  checked={!!data.per_leg_expiry}
                  onChange={(e) => handleChange("per_leg_expiry", e.target.checked)}
                />
                Different expiry per leg
              </label>
            </div>
            <div className="pbLegGrid">
              {LEGS.map((leg) => {
                const mode = data[`${leg}_strike_mode`];
                return (
                  <div className="pbLegCard" key={leg}>
                    <div className="pbLegCard__head">
                      <div className="pbLegTitle">{leg.toUpperCase()}</div>
                      <div className="pbLegPill">{mode ? mode.toUpperCase() : "MODE"}</div>
                    </div>
                    <div className="pbGrid pbGrid--2">
                      <Field
                        label="Strike mode"
                        hint="Exact strike or % from price."
                        id={`pb-edit-${uid}-${tSafe}-${leg}-mode`}
                      >
                        <select
                          value={mode || ""}
                          onChange={(e) => handleChange(`${leg}_strike_mode`, e.target.value)}
                        >
                          <option value="">Select&hellip;</option>
                          <option value="exact">Exact Strike</option>
                          <option value="pct">Strike %</option>
                          <option value="dollar">$ Deviation</option>
                        </select>
                      </Field>

                      {mode === "exact" ? (
                        <Field
                          label="Exact strike"
                          hint="Strike price (e.g., 185.00)."
                          id={`pb-edit-${uid}-${tSafe}-${leg}-exact`}
                        >
                          <input
                            type="number"
                            step="0.01"
                            value={data[`${leg}_exact_strike`] || ""}
                            onChange={(e) => handleChange(`${leg}_exact_strike`, e.target.value)}
                          />
                        </Field>
                      ) : mode === "pct" ? (
                        <Field
                          label="Strike % from price"
                          hint="e.g., 1.5 means 1.5% away."
                          id={`pb-edit-${uid}-${tSafe}-${leg}-pct`}
                        >
                          <input
                            type="number"
                            step="0.01"
                            value={data[`${leg}_strike_pct`] || ""}
                            onChange={(e) => handleChange(`${leg}_strike_pct`, e.target.value)}
                          />
                        </Field>
                      ) : mode === "dollar" ? (
                        <Field
                          label="$ deviation from price"
                          hint="Dollar deviation from current price."
                          id={`pb-edit-${uid}-${tSafe}-${leg}-dollar`}
                        >
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={data[`${leg}_dollar_dev`] || ""}
                            onChange={(e) => handleChange(`${leg}_dollar_dev`, e.target.value)}
                          />
                        </Field>
                      ) : (
                        <div className="pbHintBox">Pick a strike mode first.</div>
                      )}

                      <Field
                        label="Premium mode"
                        hint="% of stock price or fixed $ cap."
                        id={`pb-edit-${uid}-${tSafe}-${leg}-pmode`}
                      >
                        <select
                          value={data[`${leg}_premium_mode`] || "pct"}
                          onChange={(e) => handleChange(`${leg}_premium_mode`, e.target.value)}
                        >
                          <option value="pct">% of price</option>
                          <option value="dollar">$ limit</option>
                        </select>
                      </Field>

                      <Field
                        label={data[`${leg}_premium_mode`] === "dollar" ? "Premium $ cap" : "Premium %"}
                        hint={data[`${leg}_premium_mode`] === "dollar" ? "Max option price per contract." : "Max premium as % of stock price."}
                        id={`pb-edit-${uid}-${tSafe}-${leg}-prem`}
                      >
                        <input
                          type="number"
                          step="0.01"
                          value={data[`${leg}_premium_pct`] || ""}
                          onChange={(e) => handleChange(`${leg}_premium_pct`, e.target.value)}
                        />
                      </Field>

                      <Field
                        label="Direction"
                        hint="Call (C) or Put (P)."
                        id={`pb-edit-${uid}-${tSafe}-${leg}-dir`}
                      >
                        <select
                          value={data[`${leg}_direction`] || ""}
                          onChange={(e) => handleChange(`${leg}_direction`, e.target.value)}
                        >
                          <option value="">Select&hellip;</option>
                          <option value="C">C</option>
                          <option value="P">P</option>
                        </select>
                      </Field>

                      <Field
                        label="Amount to invest"
                        hint="Dollar amount for this leg."
                        id={`pb-edit-${uid}-${tSafe}-${leg}-amt`}
                      >
                        <input
                          type="number"
                          step="1"
                          value={data[`amount_to_invest_${leg}`] || ""}
                          onChange={(e) => handleChange(`amount_to_invest_${leg}`, e.target.value)}
                        />
                      </Field>

                      {data.per_leg_expiry && (
                        <Field
                          label="Expiry date"
                          hint="Option expiry for this leg."
                          id={`pb-edit-${uid}-${tSafe}-${leg}-date`}
                        >
                          <input
                            type="date"
                            value={data[`${leg}_expiry_date`] || ""}
                            onChange={(e) => handleChange(`${leg}_expiry_date`, e.target.value)}
                          />
                        </Field>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="pbModal__footer">
          <button type="button" className="pbGhostBtn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="pbPrimaryBtn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving\u2026" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

EditOrderModal.propTypes = {
  orderId: PropTypes.string.isRequired,
  ticker: PropTypes.string.isRequired,
  initialData: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/* ---------- ActiveOrders panel ---------- */

function ActiveOrders({ orders, onCancel, onEdit, onClear }) {
  if (orders.length === 0) return null;

  const hasCompleted = orders.some((o) => o.status !== "active");

  return (
    <section className="pbCard pbActiveOrders">
      <div className="pbCard__head">
        <h2 className="pbCard__title">Active Orders</h2>
        {hasCompleted && (
          <button type="button" className="pbGhostBtn" onClick={onClear}>
            Clear completed
          </button>
        )}
      </div>

      <div className="pbOrderList">
        {orders.map((o) => (
          <div
            className={`pbOrderRow pbOrderRow--${o.status}`}
            key={o.id}
          >
            <div className="pbOrderRow__ticker">{o.ticker}</div>
            <div className={`pbOrderRow__badge pbOrderRow__badge--${o.status}`}>
              {STATUS_LABELS[o.status] || o.status}
            </div>
            <div className="pbOrderRow__msg">{o.message}</div>
            <div className="pbOrderRow__time">{o.created_at}</div>
            {o.status === "active" && (
              <div className="pbOrderRow__actions">
                <button
                  type="button"
                  className="pbGhostBtn pbGhostBtn--sm"
                  onClick={() => onEdit(o.id, o.ticker)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="pbGhostBtn pbGhostBtn--sm"
                  onClick={() => onCancel(o.id)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

ActiveOrders.propTypes = {
  orders: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

/* ---------- helpers for OrderSummaryModal ---------- */

function conditionLabel(cond) {
  if (cond === ">") return "Above";
  if (cond === "<") return "Below";
  if (cond === "=") return "At";
  return cond;
}

/* ---------- OrderSummaryModal ---------- */

function OrderSummaryModal({ formData, onConfirm, onCancel }) {
  const now = new Date().toLocaleString("en-US", {
    dateStyle: "full",
    timeStyle: "short",
  });
  const tickerList = Object.entries(formData);

  return (
    <div
      className="pbModal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pb-modal-title"
    >
      <div className="pbModal">
        <div className="pbModal__header">
          <div>
            <h2 className="pbModal__title" id="pb-modal-title">
              Confirm Order Submission
            </h2>
            <p className="pbModal__subtitle">
              Review all orders carefully before confirming.
            </p>
          </div>
        </div>

        <div className="pbModal__body" id="pb-print-area">
          <div className="pbPrintHeader">
            <div className="pbPrintBrand">Price-Based Running Man</div>
            <h1 className="pbPrintTitle">Order Summary</h1>
            <div className="pbPrintMeta">
              <span>Generated: {now}</span>
              <span>
                {tickerList.length} ticker{tickerList.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {tickerList.map(([ticker, data], i) => (
            <div className="pbSummaryTicker" key={ticker}>
              <div className="pbSummaryTicker__header">
                <span className="pbSummaryTicker__num">{i + 1}</span>
                <span className="pbSummaryTicker__name"><TickerIcon symbol={ticker} size={16} /> {ticker}</span>
              </div>

              <div className="pbSummaryBody">
                <section className="pbSummarySection">
                  <div className="pbSummarySection__title">Entry Trigger</div>
                  <dl className="pbSummaryDl">
                    {data.order_start_time && (
                      <>
                        <dt>Start time</dt>
                        <dd>{data.order_start_time}</dd>
                      </>
                    )}
                    {data.order_expiry_time && (
                      <>
                        <dt>Expiry time</dt>
                        <dd>{data.order_expiry_time}</dd>
                      </>
                    )}
                    <dt>Option expiry date</dt>
                    <dd>{data.per_leg_expiry ? "Per leg" : data.expiry_date}</dd>
                    <dt>Buy condition</dt>
                    <dd>
                      {data.buy_condition === "between"
                        ? `Between $${data.buy_price} \u2013 $${data.buy_price_upper}`
                        : `${conditionLabel(data.buy_condition)} $${data.buy_price}`}
                      {data.buy_condition === "=" && data.threshold_percent
                        ? ` (\u00b1${data.threshold_percent}%)`
                        : ""}
                    </dd>
                  </dl>
                </section>

                <section className="pbSummarySection">
                  <div className="pbSummarySection__title">Legs</div>
                  <div className="pbSummaryLegs">
                    {LEGS.map((leg) => (
                      <div className="pbSummaryLeg" key={leg}>
                        <div className="pbSummaryLeg__name">{leg.toUpperCase()}</div>
                        <dl className="pbSummaryDl">
                          <dt>Direction</dt>
                          <dd>{data[`${leg}_direction`] === "C" ? "Call" : "Put"}</dd>
                          <dt>Strike</dt>
                          <dd>
                            {data[`${leg}_strike_mode`] === "exact"
                              ? `$${data[`${leg}_exact_strike`]} (exact)`
                              : data[`${leg}_strike_mode`] === "dollar"
                              ? `$${data[`${leg}_dollar_dev`]} deviation from price`
                              : `${data[`${leg}_strike_pct`]}% from price`}
                          </dd>
                          <dt>Premium</dt>
                          <dd>
                            {data[`${leg}_premium_mode`] === "dollar"
                              ? `$${data[`${leg}_premium_pct`]} cap`
                              : `${data[`${leg}_premium_pct`]}%`}
                          </dd>
                          <dt>Amount</dt>
                          <dd>${data[`amount_to_invest_${leg}`]}</dd>
                          {data.per_leg_expiry && (
                            <>
                              <dt>Expiry</dt>
                              <dd>{data[`${leg}_expiry_date`]}</dd>
                            </>
                          )}
                        </dl>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          ))}
        </div>

        <div className="pbModal__footer">
          <button type="button" className="pbGhostBtn" onClick={onCancel}>
            Go back
          </button>
          <button
            type="button"
            className="pbGhostBtn pbGhostBtn--print"
            onClick={() => window.print()}
          >
            Print
          </button>
          <button type="button" className="pbPrimaryBtn" onClick={onConfirm}>
            Confirm &amp; Submit
          </button>
        </div>
      </div>
    </div>
  );
}

OrderSummaryModal.propTypes = {
  formData: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/* ---------- main component ---------- */

export default function PriceBasedRunningMan() {
  const uid = useId();
  const [tickerInput, setTickerInput] = useState("");
  const [formData, setFormData] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [errors, setErrors] = useState(new Set());
  const [orders, setOrders] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editModal, setEditModal] = useState(null); // { orderId, ticker, data }
  const pollRef = useRef(null);

  const tickers = useMemo(() => parseTickers(tickerInput), [tickerInput]);
  const hasUnsavedData = tickers.length > 0;

  const hasError = useCallback(
    (ticker, field) => errors.has(`${ticker}.${field}`),
    [errors]
  );

  // ── in-app navigation blocker ──
  const blocker = useBlocker(hasUnsavedData);

  // ── warn on tab close / page reload ──
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedData]);

  // ── polling ──
  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/price-based-running-man/status/`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const handleCancel = async (orderId) => {
    try {
      await authFetch(`${API_BASE}/price-based-running-man/cancel/${orderId}/`, {
        method: "POST",
      });
      fetchStatus();
    } catch {
      /* silent */
    }
  };

  const handleClear = async () => {
    try {
      await authFetch(`${API_BASE}/price-based-running-man/clear/`, {
        method: "POST",
      });
      fetchStatus();
    } catch {
      /* silent */
    }
  };

  const handleEdit = async (orderId, ticker) => {
    try {
      const res = await authFetch(
        `${API_BASE}/price-based-running-man/order/${orderId}/`
      );
      if (res.ok) {
        const json = await res.json();
        setEditModal({ orderId, ticker, data: json.order.data });
      }
    } catch {
      /* silent */
    }
  };

  const handleSaveEdit = async (orderId, newData) => {
    try {
      const res = await authFetch(
        `${API_BASE}/price-based-running-man/edit/${orderId}/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: newData }),
        }
      );
      if (res.ok) {
        setEditModal(null);
        fetchStatus();
      }
    } catch {
      /* silent */
    }
  };

  // ── form handlers ──

  const handleTickerChange = (raw) => {
    setTickerInput(raw);

    const nextTickers = parseTickers(raw);
    setFormData((prev) => {
      const next = {};
      nextTickers.forEach((t) => {
        next[t] = { ...createEmptyTicker(), ...(prev[t] || {}) };
      });
      return next;
    });
  };

  const removeTicker = (ticker) => {
    const nextTickers = tickers.filter((t) => t !== ticker);
    setTickerInput(nextTickers.join(", "));
    setFormData((prev) => {
      const next = { ...prev };
      delete next[ticker];
      return next;
    });
    setErrors((prev) => {
      const next = new Set(prev);
      for (const k of prev) {
        if (k.startsWith(`${ticker}.`)) next.delete(k);
      }
      return next;
    });
  };

  const handleFieldChange = (ticker, field, value) => {
    if (field === "per_leg_expiry") {
      setFormData((prev) => ({ ...prev, [ticker]: setPerLegExpiry(prev[ticker], value) }));
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(`${ticker}.expiry_date`);
        LEGS.forEach((leg) => next.delete(`${ticker}.${leg}_expiry_date`));
        return next;
      });
      return;
    }
    setFormData((prev) => {
      const tickerData = { ...prev[ticker], [field]: value };
      if (field === "core_direction") {
        if (value === "C") {
          tickerData.lotto_direction = "C";
          tickerData.hedge_direction = "P";
        } else if (value === "P") {
          tickerData.lotto_direction = "P";
          tickerData.hedge_direction = "C";
        } else {
          tickerData.lotto_direction = "";
          tickerData.hedge_direction = "";
        }
      }
      return { ...prev, [ticker]: tickerData };
    });
    const errKey = `${ticker}.${field}`;
    if (errors.has(errKey)) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(errKey);
        if (field === "core_direction") {
          next.delete(`${ticker}.lotto_direction`);
          next.delete(`${ticker}.hedge_direction`);
        }
        return next;
      });
    } else if (field === "core_direction" && value) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(`${ticker}.lotto_direction`);
        next.delete(`${ticker}.hedge_direction`);
        return next;
      });
    }
  };

  // validate → show confirm modal (actual submission happens in handleConfirmedSubmit)
  const handleSubmit = (e) => {
    e.preventDefault();

    const errs = validateForm(formData);
    setErrors(errs);
    if (errs.size > 0) {
      setStatus({
        state: "error",
        message: `${errs.size} required field${errs.size > 1 ? "s" : ""} missing`,
      });
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);

    const payload = {
      price_based_tickers: Object.keys(formData),
      data: {},
    };
    Object.entries(formData).forEach(([ticker, fields]) => {
      payload.data[ticker] = { ...fields };
    });

    setStatus({ state: "loading", message: "Submitting\u2026" });

    try {
      const res = await authFetch(
        `${API_BASE}/price-based-running-man/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || "Request failed");
      }

      const ids = data.order_ids || {};
      const submitted = Object.keys(ids).join(", ");
      setStatus({ state: "success", message: `Submitted: ${submitted}` });
      fetchStatus();
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.message || "Error submitting strategy",
      });
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-header__title">Price-Based Running Man</h2>
        <p className="page-header__subtitle">
          Add tickers, configure entry trigger + legs, then submit to run.
          You can submit multiple orders — they run concurrently.
        </p>
      </div>

      <ActiveOrders
        orders={orders}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onClear={handleClear}
      />

      <form onSubmit={handleSubmit} className="pbForm">
        <section className="pbCard">
          <div className="pbCard__head">
            <h2 className="pbCard__title">Tickers</h2>
            <div className="pbMeta">{tickers.length} ticker(s)</div>
          </div>

          <div className="pbGrid pbGrid--2">
            <Field
              label="Tickers (comma-separated)"
              hint="Example: AAPL, MSFT, SPY"
              id={`pb-${uid}-tickers`}
            >
              <TickerAutocomplete
                value={tickerInput}
                onChange={handleTickerChange}
                placeholder="AAPL, MSFT"
              />
            </Field>

            <div className="pbChips" aria-label="Current tickers">
              {tickers.length === 0 ? (
                <div className="pbHintBox">Add at least one ticker to begin.</div>
              ) : (
                tickers.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="pbChip"
                    onClick={() => removeTicker(t)}
                    title={`Remove ${t}`}
                  >
                    <TickerIcon symbol={t} size={16} />
                    <span className="pbChip__text">{t}</span>
                    <span className="pbChip__x" aria-hidden="true">
                      &times;
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </section>

        {Object.keys(formData).map((ticker) => {
          const tSafe = safeId(ticker);
          const data = formData[ticker];

          return (
            <section className={`pbTickerCard${data.core_direction === "C" ? " pbTickerCard--call" : data.core_direction === "P" ? " pbTickerCard--put" : ""}`} key={ticker}>
              <div className="pbTickerCard__head">
                <div className="pbTickerCard__left">
                  <div className="pbTicker"><TickerLabel symbol={ticker} /></div>
                  <div className="pbTickerSub">Configure trigger + legs</div>
                </div>

                <button
                  type="button"
                  className="pbGhostBtn"
                  onClick={() => removeTicker(ticker)}
                >
                  Remove
                </button>
              </div>

              <div className="pbSection">
                <div className="pbSection__title">Entry trigger</div>

                <div className="pbGrid pbGrid--3">
                  <Field
                    label="Order start time (optional)"
                    hint="Leave blank to start checking immediately."
                    id={`pb-${uid}-${tSafe}-start`}
                  >
                    <input
                      type="time"
                      step="1"
                      value={data.order_start_time || ""}
                      onChange={(e) =>
                        handleFieldChange(ticker, "order_start_time", e.target.value)
                      }
                    />
                  </Field>

                  <Field
                    label="Order expiry time (optional)"
                    hint="Leave blank to keep checking until executed."
                    id={`pb-${uid}-${tSafe}-expirytime`}
                  >
                    <input
                      type="time"
                      step="1"
                      value={data.order_expiry_time || ""}
                      onChange={(e) =>
                        handleFieldChange(ticker, "order_expiry_time", e.target.value)
                      }
                    />
                  </Field>

                  <Field
                    label="Expiry date"
                    hint={data.per_leg_expiry ? "Set per leg below." : "Option expiry date."}
                    id={`pb-${uid}-${tSafe}-date`}
                    error={hasError(ticker, "expiry_date")}
                  >
                    <input
                      type="date"
                      value={data.expiry_date || ""}
                      disabled={!!data.per_leg_expiry}
                      onChange={(e) =>
                        handleFieldChange(ticker, "expiry_date", e.target.value)
                      }
                    />
                  </Field>

                  <Field
                    label="Buy condition"
                    hint="Choose how price should compare."
                    id={`pb-${uid}-${tSafe}-cond`}
                    error={hasError(ticker, "buy_condition")}
                  >
                    <select
                      value={data.buy_condition || ""}
                      onChange={(e) =>
                        handleFieldChange(ticker, "buy_condition", e.target.value)
                      }
                    >
                      <option value="">Select&hellip;</option>
                      <option value=">">&gt; Above</option>
                      <option value="<">&lt; Below</option>
                      <option value="=">= At</option>
                      <option value="between">Between</option>
                    </select>
                  </Field>

                  {data.buy_condition === "between" ? (
                    <>
                      <Field
                        label="Lower price (&ge;)"
                        hint="Buy when price is at or above this."
                        id={`pb-${uid}-${tSafe}-price`}
                        error={hasError(ticker, "buy_price")}
                      >
                        <input
                          type="number"
                          step="0.01"
                          value={data.buy_price || ""}
                          onChange={(e) =>
                            handleFieldChange(ticker, "buy_price", e.target.value)
                          }
                        />
                      </Field>

                      <Field
                        label="Upper price (&le;)"
                        hint="Buy when price is at or below this."
                        id={`pb-${uid}-${tSafe}-price-upper`}
                        error={hasError(ticker, "buy_price_upper")}
                      >
                        <input
                          type="number"
                          step="0.01"
                          value={data.buy_price_upper || ""}
                          onChange={(e) =>
                            handleFieldChange(ticker, "buy_price_upper", e.target.value)
                          }
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field
                        label="Buy price"
                        hint="Target price for the condition."
                        id={`pb-${uid}-${tSafe}-price`}
                        error={hasError(ticker, "buy_price")}
                      >
                        <input
                          type="number"
                          step="0.01"
                          value={data.buy_price || ""}
                          onChange={(e) =>
                            handleFieldChange(ticker, "buy_price", e.target.value)
                          }
                        />
                      </Field>

                      {data.buy_condition === "=" ? (
                        <Field
                          label="Threshold %"
                          hint="Allowed % deviation when using '='."
                          id={`pb-${uid}-${tSafe}-threshold`}
                          error={hasError(ticker, "threshold_percent")}
                        >
                          <input
                            type="number"
                            step="0.01"
                            value={data.threshold_percent || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                ticker,
                                "threshold_percent",
                                e.target.value
                              )
                            }
                          />
                        </Field>
                      ) : (
                        <div className="pbHintBox">
                          Threshold only applies when condition is &quot;At (=)&quot;.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="pbSection">
                <div className="pbSection__head">
                  <div className="pbSection__title">Legs</div>
                  <label className="pbCheck">
                    <input
                      type="checkbox"
                      checked={!!data.per_leg_expiry}
                      onChange={(e) =>
                        handleFieldChange(ticker, "per_leg_expiry", e.target.checked)
                      }
                    />
                    Different expiry per leg
                  </label>
                </div>

                <div className="pbLegGrid">
                  {LEGS.map((leg) => {
                    const mode = data[`${leg}_strike_mode`];

                    return (
                      <div className="pbLegCard" key={leg}>
                        <div className="pbLegCard__head">
                          <div className="pbLegTitle">{leg.toUpperCase()}</div>
                          <div className="pbLegPill">
                            {mode ? mode.toUpperCase() : "MODE"}
                          </div>
                        </div>

                        <div className="pbGrid pbGrid--2">
                          <Field
                            label="Strike mode"
                            hint="Exact strike or % from price."
                            id={`pb-${uid}-${tSafe}-${leg}-mode`}
                            error={hasError(ticker, `${leg}_strike_mode`)}
                          >
                            <select
                              value={mode || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  ticker,
                                  `${leg}_strike_mode`,
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Select&hellip;</option>
                              <option value="exact">Exact Strike</option>
                              <option value="pct">Strike %</option>
                              <option value="dollar">$ Deviation</option>
                            </select>
                          </Field>

                          {mode === "exact" ? (
                            <Field
                              label="Exact strike"
                              hint="Strike price (e.g., 185.00)."
                              id={`pb-${uid}-${tSafe}-${leg}-exact`}
                              error={hasError(ticker, `${leg}_exact_strike`)}
                            >
                              <input
                                type="number"
                                step="0.01"
                                value={data[`${leg}_exact_strike`] || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    ticker,
                                    `${leg}_exact_strike`,
                                    e.target.value
                                  )
                                }
                              />
                            </Field>
                          ) : mode === "pct" ? (
                            <Field
                              label="Strike % from price"
                              hint="e.g., 1.5 means 1.5% away."
                              id={`pb-${uid}-${tSafe}-${leg}-pct`}
                              error={hasError(ticker, `${leg}_strike_pct`)}
                            >
                              <input
                                type="number"
                                step="0.01"
                                value={data[`${leg}_strike_pct`] || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    ticker,
                                    `${leg}_strike_pct`,
                                    e.target.value
                                  )
                                }
                              />
                            </Field>
                          ) : mode === "dollar" ? (
                            <Field
                              label="$ deviation from price"
                              hint="Call: picks strike closest to price + $X (≥ ATM). Put: closest to price − $X (≤ ATM)."
                              id={`pb-${uid}-${tSafe}-${leg}-dollar`}
                              error={hasError(ticker, `${leg}_dollar_dev`)}
                            >
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={data[`${leg}_dollar_dev`] || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    ticker,
                                    `${leg}_dollar_dev`,
                                    e.target.value
                                  )
                                }
                              />
                            </Field>
                          ) : (
                            <div className="pbHintBox">Pick a strike mode first.</div>
                          )}

                          <Field
                            label="Premium mode"
                            hint="% of stock price or fixed $ cap."
                            id={`pb-${uid}-${tSafe}-${leg}-pmode`}
                          >
                            <select
                              value={data[`${leg}_premium_mode`] || "pct"}
                              onChange={(e) =>
                                handleFieldChange(
                                  ticker,
                                  `${leg}_premium_mode`,
                                  e.target.value
                                )
                              }
                            >
                              <option value="pct">% of price</option>
                              <option value="dollar">$ limit</option>
                            </select>
                          </Field>

                          <Field
                            label={data[`${leg}_premium_mode`] === "dollar" ? "Premium $ cap" : "Premium %"}
                            hint={data[`${leg}_premium_mode`] === "dollar" ? "Max option price per contract." : "Max premium as % of stock price."}
                            id={`pb-${uid}-${tSafe}-${leg}-prem`}
                            error={hasError(ticker, `${leg}_premium_pct`)}
                          >
                            <input
                              type="number"
                              step="0.01"
                              value={data[`${leg}_premium_pct`] || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  ticker,
                                  `${leg}_premium_pct`,
                                  e.target.value
                                )
                              }
                            />
                          </Field>

                          <Field
                            label={leg !== "core" && data.core_direction ? "Direction (auto)" : "Direction"}
                            hint="Call (C) or Put (P)."
                            id={`pb-${uid}-${tSafe}-${leg}-dir`}
                            error={hasError(ticker, `${leg}_direction`)}
                          >
                            <select
                              value={data[`${leg}_direction`] || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  ticker,
                                  `${leg}_direction`,
                                  e.target.value
                                )
                              }
                              disabled={leg !== "core" && !!data.core_direction}
                            >
                              <option value="">Select&hellip;</option>
                              <option value="C">C</option>
                              <option value="P">P</option>
                            </select>
                          </Field>

                          <Field
                            label="Amount to invest"
                            hint="Dollar amount for this leg."
                            id={`pb-${uid}-${tSafe}-${leg}-amt`}
                            error={hasError(ticker, `amount_to_invest_${leg}`)}
                          >
                            <input
                              type="number"
                              step="1"
                              value={data[`amount_to_invest_${leg}`] || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  ticker,
                                  `amount_to_invest_${leg}`,
                                  e.target.value
                                )
                              }
                            />
                          </Field>

                          {data.per_leg_expiry && (
                            <Field
                              label="Expiry date"
                              hint="Option expiry for this leg."
                              id={`pb-${uid}-${tSafe}-${leg}-date`}
                              error={hasError(ticker, `${leg}_expiry_date`)}
                            >
                              <input
                                type="date"
                                value={data[`${leg}_expiry_date`] || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    ticker,
                                    `${leg}_expiry_date`,
                                    e.target.value
                                  )
                                }
                              />
                            </Field>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}

        <div className="pbActionBar">
          <div className={`pbStatus pbStatus--${status.state}`}>
            {status.state === "idle" ? (
              <span className="pbStatus__muted">Ready</span>
            ) : (
              status.message
            )}
          </div>

          <button
            className="pbPrimaryBtn"
            type="submit"
            disabled={status.state === "loading" || tickers.length === 0}
          >
            {status.state === "loading" ? "Submitting\u2026" : "Submit Strategy"}
          </button>
        </div>
      </form>

      {/* Edit order modal */}
      {editModal && (
        <EditOrderModal
          orderId={editModal.orderId}
          ticker={editModal.ticker}
          initialData={editModal.data}
          onSave={handleSaveEdit}
          onClose={() => setEditModal(null)}
        />
      )}

      {/* Order confirmation modal */}
      {showConfirmModal && (
        <OrderSummaryModal
          formData={formData}
          onConfirm={handleConfirmedSubmit}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {/* In-app navigation guard */}
      {blocker.state === "blocked" && (
        <div
          className="pbModal__backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pb-nav-title"
        >
          <div className="pbModal pbModal--sm">
            <div className="pbModal__header">
              <h2 className="pbModal__title" id="pb-nav-title">
                Leave page?
              </h2>
              <p className="pbModal__subtitle">
                You have unsaved order data. Leaving now will discard everything
                you&apos;ve entered.
              </p>
            </div>
            <div className="pbModal__footer">
              <button
                type="button"
                className="pbGhostBtn"
                onClick={() => blocker.reset()}
              >
                Stay on page
              </button>
              <button
                type="button"
                className="pbDangerBtn"
                onClick={() => blocker.proceed()}
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
